import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import type { WhatWeDoService } from '../../data/whatWeDo';

/*
 * ServiceCard
 * -----------
 * Single card in the "Explore how we can help" grid on /services AND
 * in the "Explore our other services" grid at the bottom of each
 * /services/<slug> detail page. Shared so the two surfaces stay
 * visually identical automatically — bump the type or padding once
 * and both pages update.
 *
 * Uses hover-to-play semantics: at rest each video is paused (the
 * browser still renders the first frame after our on-mount "prime and
 * pause" trick), and only the hovered card actually decodes/plays.
 * That keeps the page snappy even when a whole grid of cards is on
 * screen at once.
 */

export interface ServiceCardProps {
  service: WhatWeDoService;
  /**
   * Delay (seconds) applied to the entrance animation so a whole grid
   * doesn't pop in at once. Callers typically pass a staggered value
   * (e.g. `Math.min(i, 5) * 0.06`).
   */
  animationDelay?: number;
}

export const ServiceCard: React.FC<ServiceCardProps> = ({
  service,
  animationDelay = 0,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Prime the first frame on mount so idle cards don't sit on a blank
  // black rectangle waiting for hover. We briefly kick playback and
  // then immediately pause so the browser has decoded frame 0.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.then === 'function') {
      p.then(() => {
        // The user might have hovered in during this microtask — only
        // pause if we're still the "idle" state (no hover in flight).
        if (!v.dataset.hovering) v.pause();
      }).catch(() => {
        // Autoplay blocked (rare with muted, but happens). The card
        // still shows the poster / first-frame placeholder background.
      });
    }
  }, [service.mediaUrl]);

  const handleEnter = () => {
    const v = videoRef.current;
    if (!v) return;
    v.dataset.hovering = 'true';
    v.play().catch(() => {});
  };

  const handleLeave = () => {
    const v = videoRef.current;
    if (!v) return;
    delete v.dataset.hovering;
    v.pause();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{
        duration: 0.45,
        delay: animationDelay,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      <Link
        to={`/services/${service.id}`}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
        className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 rounded-xl"
      >
        {/* Media panel — 16:9 aspect ratio with a subtle scale-in on
            hover so the card gains a bit of "lift" without needing a
            border/shadow shift. */}
        <div className="aspect-video overflow-hidden rounded-xl bg-navy-900 mb-5">
          <video
            ref={videoRef}
            src={service.mediaUrl}
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </div>

        <h3 className="text-xl md:text-[22px] font-bold text-navy-900 mb-2.5 group-hover:text-accent-700 transition-colors">
          {service.title}
        </h3>

        {/* `line-clamp-3` keeps every card visually the same height even
            though the underlying `heroDescription` varies from 1 to 3
            sentences across services. */}
        <p className="text-[15px] md:text-base text-navy-600 leading-relaxed mb-4 line-clamp-3">
          {service.heroDescription}
        </p>

        <span className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-accent-600 group-hover:text-accent-700 transition-colors">
          Learn more
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </span>
      </Link>
    </motion.div>
  );
};
